import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    Row,
    Col,
    Form,
    InputGroup,
    Badge,
    Button,
    Tabs,
    Tab,
    Spinner,
} from "react-bootstrap";
import { IconBox, IconFileDescription, IconArrowLeft } from "@tabler/icons-react";
import { toast } from "react-toastify";
import { API_ENDPOINTS, roundToMoney } from "../../constant/_constant";
import moment from "moment";
import usePostRequest from "../../hooks/usePostRequest";
import { trimTrailingZeros } from "../../utils/_helpers";
import { useParams, useNavigate } from "react-router-dom";
import DynamicPagination from "../../components/DynamicPagination ";

const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
};

const getPreviousDate = (dateStr) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() - 1);
    return formatDate(d.toISOString().split("T")[0]);
};

function YearPriceCard({ entry, idx, globalIndex, onPriceChange }) {
    return (
        <Col xl={4} lg={6} md={6}>
            <Card className="h-100 border shadow-none">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <Badge bg="primary" className="mb-2">
                                Year {idx + 1}
                            </Badge>
                            <div className="fw-semibold">
                                {formatDate(entry.from_date)} - {getPreviousDate(entry.to_date)}
                            </div>
                        </div>
                    </div>

                    <Row className="g-3">
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label className="small fw-semibold text-muted">
                                    Customer Price
                                </Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="any"
                                        value={entry.customer_price}
                                        onChange={(e) =>
                                            onPriceChange(globalIndex, "customer_price", e.target.value)
                                        }
                                        required
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label className="small fw-semibold text-muted">
                                    Cost
                                </Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>$</InputGroup.Text>
                                    <Form.Control
                                        type="number"
                                        step="any"
                                        value={entry.aspetto_price}
                                        onChange={(e) =>
                                            onPriceChange(globalIndex, "aspetto_price", e.target.value)
                                        }
                                        required
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </Col>
    );
}

function KitPriceChanges() {
    const { rfqId } = useParams();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("products");

    // ---- Products YoY ----
    const { data: productsRes, isLoading: productsLoading, postData: fetchProductsInfo } =
        usePostRequest(API_ENDPOINTS.GET_PRICE_CHANGES);
    const { isLoading: productsSaving, postData: saveProductsPriceChanges } =
        usePostRequest(API_ENDPOINTS.PRICE_CHANGES);

    const productsInfo = productsRes?.data;
    const productsDuration = Number(productsInfo?.project_duration);
    const productsYearCount =
        isNaN(productsDuration) || productsDuration <= 0 ? 0 : productsDuration;

    const [productsMargin, setProductsMargin] = useState(0);
    const [productsPriceChanges, setProductsPriceChanges] = useState([]);
    const [productsPage, setProductsPage] = useState(1);
    const [productsLimit, setProductsLimit] = useState(10);

    useEffect(() => {
        fetchProductsInfo({ rfq_id: rfqId });
    }, []);

    useEffect(() => {
        const products = productsInfo?.RfqProducts || [];
        const existingChanges = productsInfo?.RfqPriceChanges || [];
        const baseDate = productsInfo?.start_date ? moment(productsInfo.start_date) : moment();
        if (products.length === 0 || productsYearCount === 0) return;
        const allChanges = [];

        products.forEach((product) => {
            const productSku = product.sku_id;
            const rawSku = product.raw_sku_id;
            const productBasePrice = parseFloat(product.customer_price) || 0;
            const productBaseAspettoPrice = parseFloat(product.aspetto_price) || 0;
            const skuChanges = existingChanges.filter((c) => c.raw_sku_id === rawSku);
            const hasExistingChanges = skuChanges.length > 0;

            for (let yearIndex = 0; yearIndex < productsYearCount; yearIndex++) {
                const fromDate = baseDate.clone().add(yearIndex, "years").format("YYYY-MM-DD");
                const toDate = baseDate.clone().add(yearIndex + 1, "years").format("YYYY-MM-DD");

                const existing = skuChanges.find(
                    (change) => change.from_date === fromDate && change.to_date === toDate
                );

                if (existing) {
                    allChanges.push({
                        ...existing,
                        customer_price: trimTrailingZeros(existing.customer_price),
                        aspetto_price: trimTrailingZeros(existing.aspetto_price),
                    });
                } else if (!hasExistingChanges) {
                    let price;
                    let aspettoPrice;
                    if (yearIndex === 0) {
                        price = productBasePrice;
                        aspettoPrice = productBaseAspettoPrice;
                    } else {
                        const prevPriceChange = allChanges.find(
                            (change) =>
                                change.raw_sku_id === rawSku &&
                                change.from_date ===
                                baseDate.clone().add(yearIndex - 1, "years").format("YYYY-MM-DD")
                        );
                        const prevPrice = prevPriceChange
                            ? parseFloat(prevPriceChange.customer_price) || 0
                            : productBasePrice;
                        const prevAspettoPrice = prevPriceChange
                            ? parseFloat(prevPriceChange.aspetto_price) || 0
                            : productBaseAspettoPrice;
                        price = roundToMoney(prevPrice + prevPrice * (productsMargin / 100), 4);
                        aspettoPrice = roundToMoney(
                            prevAspettoPrice + prevAspettoPrice * (productsMargin / 100),
                            4
                        );
                    }

                    allChanges.push({
                        sku_id: productSku,
                        raw_sku_id: rawSku,
                        customer_price: trimTrailingZeros(price),
                        aspetto_price: trimTrailingZeros(aspettoPrice),
                        margin_percent: productsMargin,
                        from_date: fromDate,
                        to_date: toDate,
                    });
                } else {
                    allChanges.push({
                        sku_id: productSku,
                        raw_sku_id: rawSku,
                        customer_price: "",
                        aspetto_price: "",
                        margin_percent: productsMargin,
                        from_date: fromDate,
                        to_date: toDate,
                    });
                }
            }
        });

        setProductsPriceChanges(allChanges);
    }, [productsInfo, productsMargin, productsYearCount]);

    const handleProductsPriceChange = (index, field, value) => {
        const updated = [...productsPriceChanges];
        updated[index][field] = trimTrailingZeros(value);
        setProductsPriceChanges(updated);
    };

    const handleProductsSubmit = (e) => {
        e.preventDefault();
        if (!e.target.checkValidity()) {
            e.stopPropagation();
            toast.error("Please fill all required fields.");
            return;
        }
        saveProductsPriceChanges(
            {
                rfq_id: productsInfo?.rfq_id,
                price_changes: productsPriceChanges,
                opportunity_type: productsInfo?.opportunity_type,
                rfq_status: productsInfo?.status,
            },
            () => toast.success("Price Changes Updated successfully!"),
            (error) => console.error("Error response:", error)
        );
    };

    const productsList = productsInfo?.RfqProducts || [];
    const productsTotalPages = Math.max(1, Math.ceil(productsList.length / productsLimit));
    const paginatedProducts = productsList.slice(
        (productsPage - 1) * productsLimit,
        productsPage * productsLimit
    );

    const handleProductsLimitChange = (limit) => {
        setProductsLimit(limit);
        setProductsPage(1);
    };

    // ---- Kits YoY ----
    const [kitsFetched, setKitsFetched] = useState(false);
    const { data: kitsRes, isLoading: kitsLoading, postData: fetchKitsInfo } = usePostRequest(
        API_ENDPOINTS.KIT_PRICE_CHANGES_LISTING
    );
    const { isLoading: kitsSaving, postData: saveKitsPriceChanges } = usePostRequest(
        API_ENDPOINTS.KIT_PRICE_CHANGES_UPDATE
    );

    const kitsInfo = kitsRes?.data;
    const kitsDuration = Number(kitsInfo?.project_duration);
    const kitsYearCount = isNaN(kitsDuration) || kitsDuration <= 0 ? 0 : kitsDuration;

    const [kitsMargin, setKitsMargin] = useState(0);
    const [kitsPriceChanges, setKitsPriceChanges] = useState([]);
    const [kitsPage, setKitsPage] = useState(1);
    const [kitsLimit, setKitsLimit] = useState(10);

    const handleTabSelect = (key) => {
        setActiveTab(key);
        if (key === "kits" && !kitsFetched) {
            setKitsFetched(true);
            fetchKitsInfo({ rfq_id: rfqId });
        }
    };

    useEffect(() => {
        const products = (kitsInfo?.RfqProducts || []).flatMap(
            (kit) => kit?.Product?.ProductKits || []
        );
        const existingChanges = kitsInfo?.RfqKitPriceChanges || [];
        const baseDate = kitsInfo?.start_date ? moment(kitsInfo.start_date) : moment();
        if (products.length === 0 || kitsYearCount === 0) return;
        const allChanges = [];

        products.forEach((product) => {
            const productSku = product.sku_id;
            const rawSku = product.raw_sku_id;
            const kitId = String(product.product_id);
            const productBasePrice = parseFloat(product.customer_price) || 0;
            const productBaseAspettoPrice = parseFloat(product.aspetto_price) || 0;
            const skuChanges = existingChanges.filter((c) => c.raw_sku_id === rawSku);
            const hasExistingChanges = skuChanges.length > 0;

            for (let yearIndex = 0; yearIndex < kitsYearCount; yearIndex++) {
                const fromDate = baseDate.clone().add(yearIndex, "years").format("YYYY-MM-DD");
                const toDate = baseDate.clone().add(yearIndex + 1, "years").format("YYYY-MM-DD");

                const existing = skuChanges.find(
                    (change) => change.from_date === fromDate && change.to_date === toDate
                );

                if (existing) {
                    allChanges.push({
                        ...existing,
                        kit_id: kitId,
                        customer_price: trimTrailingZeros(existing.customer_price),
                        aspetto_price: trimTrailingZeros(existing.aspetto_price),
                    });
                } else if (!hasExistingChanges) {
                    let price;
                    let aspettoPrice;
                    if (yearIndex === 0) {
                        price = productBasePrice;
                        aspettoPrice = productBaseAspettoPrice;
                    } else {
                        const prevPriceChange = allChanges.find(
                            (change) =>
                                change.raw_sku_id === rawSku &&
                                change.from_date ===
                                baseDate.clone().add(yearIndex - 1, "years").format("YYYY-MM-DD")
                        );
                        const prevPrice = prevPriceChange
                            ? parseFloat(prevPriceChange.customer_price) || 0
                            : productBasePrice;
                        const prevAspettoPrice = prevPriceChange
                            ? parseFloat(prevPriceChange.aspetto_price) || 0
                            : productBaseAspettoPrice;
                        price = roundToMoney(prevPrice + prevPrice * (kitsMargin / 100), 4);
                        aspettoPrice = roundToMoney(
                            prevAspettoPrice + prevAspettoPrice * (kitsMargin / 100),
                            4
                        );
                    }

                    allChanges.push({
                        sku_id: productSku,
                        raw_sku_id: rawSku,
                        kit_id: kitId,
                        customer_price: trimTrailingZeros(price),
                        aspetto_price: trimTrailingZeros(aspettoPrice),
                        margin_percent: kitsMargin,
                        from_date: fromDate,
                        to_date: toDate,
                    });
                } else {
                    allChanges.push({
                        sku_id: productSku,
                        raw_sku_id: rawSku,
                        kit_id: kitId,
                        customer_price: "",
                        aspetto_price: "",
                        margin_percent: kitsMargin,
                        from_date: fromDate,
                        to_date: toDate,
                    });
                }
            }
        });

        setKitsPriceChanges(allChanges);
    }, [kitsInfo, kitsMargin, kitsYearCount]);

    const handleKitsPriceChange = (index, field, value) => {
        const updated = [...kitsPriceChanges];
        updated[index][field] = trimTrailingZeros(value);
        setKitsPriceChanges(updated);
    };

    const handleKitsSubmit = (e) => {
        e.preventDefault();
        if (!e.target.checkValidity()) {
            e.stopPropagation();
            toast.error("Please fill all required fields.");
            return;
        }
        saveKitsPriceChanges(
            {
                rfq_id: kitsInfo?.rfq_id,
                price_changes: kitsPriceChanges,
                opportunity_type: kitsInfo?.opportunity_type,
                rfq_status: kitsInfo?.status,
            },
            () => toast.success("Kit Price Changes Updated successfully!"),
            (error) => console.error("Error response:", error)
        );
    };

    // Each RfqProducts row is one Kit; the products priced/year-wise inside that kit
    // are its components, listed under Product.ProductKits.
    const kits = useMemo(() => {
        const rows = kitsInfo?.RfqProducts || [];
        return rows.map((row) => ({
            kitName: row?.sku_display_name,
            products: row?.Product?.ProductKits || [],
        }));
    }, [kitsInfo]);

    const kitsTotalPages = Math.max(1, Math.ceil(kits.length / kitsLimit));
    const paginatedKits = kits.slice((kitsPage - 1) * kitsLimit, kitsPage * kitsLimit);

    const handleKitsLimitChange = (limit) => {
        setKitsLimit(limit);
        setKitsPage(1);
    };

    return (
        <div className="p-3" style={{ background: "#f8f9fb" }}>
            <Button
                type="button"
                size="sm"
                className="d-flex align-items-center gap-1 btn btn-warning mb-3"
                onClick={() => navigate(-1)}
            >
                <IconArrowLeft size={16} />
                Back
            </Button>

            <h5 className="fw-semibold mb-3">Price Changes</h5>

            <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="mb-3" mountOnEnter>
                <Tab eventKey="products" title="YoY">
                    {productsLoading && !productsInfo ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Form onSubmit={handleProductsSubmit}>
                            <Row className="align-items-center g-3 mb-4">
                                <Col md={8}>
                                    <div className="text-muted small">
                                        Manage customer and Aspetto prices year-wise for all products.
                                    </div>
                                </Col>
                                <Col md={4} lg={3} className="ms-auto">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold small mb-1">
                                            Global Margin %
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={productsMargin || ""}
                                                onChange={(e) =>
                                                    setProductsMargin(
                                                        e.target.value ? parseFloat(e.target.value) : 0
                                                    )
                                                }
                                                placeholder="Enter Margin"
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {!productsInfo?.project_duration ? (
                                <div className="alert alert-warning mb-0">
                                    ⚠️ Project duration is not set.
                                </div>
                            ) : !productsList.length ? (
                                <div className="alert alert-info mb-0">ℹ No products available to show.</div>
                            ) : (
                                <>
                                    {paginatedProducts.map((product) => {
                                        const productPriceChanges = productsPriceChanges.filter(
                                            (p) => p.raw_sku_id === product.raw_sku_id
                                        );

                                        return (
                                            <Card className="border-0 shadow-sm mb-3" key={product.raw_sku_id}>
                                                <Card.Header
                                                    className="bg-white border-0 py-3"
                                                    style={{ borderBottom: "1px solid #edf0f3" }}
                                                >
                                                    <div className="d-flex gap-2 align-items-start">
                                                        <div
                                                            className="d-flex align-items-center justify-content-center rounded"
                                                            style={{
                                                                width: 34,
                                                                height: 34,
                                                                background: "#eef3ff",
                                                                color: "#52677a",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <IconFileDescription size={20} />
                                                        </div>

                                                        <div className="w-100">
                                                            <div className="d-flex flex-wrap align-items-center gap-2">
                                                                <div className="fw-semibold text-dark">
                                                                    {product?.sku_display_name}
                                                                </div>
                                                                {product?.Vendor?.display_name && (
                                                                    <Badge bg="light" text="dark" className="border">
                                                                        {product?.Vendor?.display_name}
                                                                    </Badge>
                                                                )}
                                                            </div>

                                                            {product?.description && (
                                                                <div
                                                                    className="text-muted small mt-1"
                                                                    style={{ textTransform: "capitalize" }}
                                                                >
                                                                    {product?.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Card.Header>

                                                <Card.Body className="pt-0">
                                                    <Row className="g-3">
                                                        {productPriceChanges.map((entry, idx) => {
                                                            const globalIndex = productsPriceChanges.findIndex(
                                                                (p) =>
                                                                    p.raw_sku_id === entry.raw_sku_id &&
                                                                    p.from_date === entry.from_date
                                                            );

                                                            return (
                                                                <YearPriceCard
                                                                    key={idx}
                                                                    entry={entry}
                                                                    idx={idx}
                                                                    globalIndex={globalIndex}
                                                                    onPriceChange={handleProductsPriceChange}
                                                                />
                                                            );
                                                        })}
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        );
                                    })}

                                    {productsTotalPages > 1 && (
                                        <DynamicPagination
                                            totalPages={productsTotalPages}
                                            currentPage={productsPage}
                                            onPageChange={setProductsPage}
                                            limitPerPage={productsLimit}
                                            onLimitChange={handleProductsLimitChange}
                                        />
                                    )}

                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <Button type="button" variant="light" onClick={() => navigate(-1)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" variant="primary" disabled={productsSaving}>
                                            {productsSaving ? "Saving..." : "Save Price Changes"}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Form>
                    )}
                </Tab>

                <Tab eventKey="kits" title="Kits YoY">
                    {!kitsFetched || (kitsLoading && !kitsInfo) ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <Form onSubmit={handleKitsSubmit}>
                            <Row className="align-items-center g-3 mb-4">
                                <Col md={8}>
                                    <div className="text-muted small">
                                        Manage customer and Aspetto prices year-wise for all kit products.
                                    </div>
                                </Col>
                                <Col md={4} lg={3} className="ms-auto">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold small mb-1">
                                            Global Margin %
                                        </Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                value={kitsMargin || ""}
                                                onChange={(e) =>
                                                    setKitsMargin(e.target.value ? parseFloat(e.target.value) : 0)
                                                }
                                                placeholder="Enter Margin"
                                            />
                                            <InputGroup.Text>%</InputGroup.Text>
                                        </InputGroup>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {!kitsInfo?.project_duration ? (
                                <div className="alert alert-warning mb-0">
                                    ⚠️ Project duration is not set.
                                </div>
                            ) : !kits.length ? (
                                <div className="alert alert-info mb-0">ℹ No products available to show.</div>
                            ) : (
                                <>
                                    {paginatedKits.map((kit, kitIdx) => (
                                        <Card className="border-0 shadow-sm mb-4" key={kitIdx}>
                                            <Card.Header className="bg-white d-flex flex-wrap align-items-center justify-content-between gap-2 py-3">
                                                <div className="d-flex align-items-center gap-2">
                                                    <IconBox size={20} className="text-primary" />
                                                    <span className="fw-bold text-uppercase">
                                                        KIT: {kit.kitName}
                                                    </span>
                                                </div>
                                                <Badge bg="light" text="dark" className="border">
                                                    {kit.products.length} Product
                                                    {kit.products.length > 1 ? "s" : ""}
                                                </Badge>
                                            </Card.Header>

                                            <Card.Body className="pt-3" style={{ background: "#f8f9fb" }}>
                                                {kit?.products?.map((product, productIdx) => {
                                                    const productPriceChanges = kitsPriceChanges.filter(
                                                        (p) => p.raw_sku_id === product.raw_sku_id
                                                    );

                                                    return (
                                                        <Card className="border-0 shadow-sm mb-3" key={productIdx}>
                                                            <Card.Header
                                                                className="bg-white border-0 py-3"
                                                                style={{ borderBottom: "1px solid #edf0f3" }}
                                                            >
                                                                <div className="d-flex gap-2 align-items-start">
                                                                    <div
                                                                        className="d-flex align-items-center justify-content-center rounded"
                                                                        style={{
                                                                            width: 34,
                                                                            height: 34,
                                                                            background: "#eef3ff",
                                                                            color: "#52677a",
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        <IconFileDescription size={20} />
                                                                    </div>

                                                                    <div className="w-100">
                                                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                                                            <div className="fw-semibold text-dark">
                                                                                {product?.sku_display_name}
                                                                            </div>
                                                                            {product?.display_name && (
                                                                                <Badge bg="light" text="dark" className="border">
                                                                                    {product?.display_name}
                                                                                </Badge>
                                                                            )}
                                                                        </div>

                                                                        {product?.description && (
                                                                            <div className="text-muted small mt-1">
                                                                                {product?.description}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </Card.Header>

                                                            <Card.Body className="pt-0">
                                                                <Row className="g-3">
                                                                    {productPriceChanges.map((entry, idx) => {
                                                                        const globalIndex = kitsPriceChanges.findIndex(
                                                                            (p) =>
                                                                                p.raw_sku_id === entry.raw_sku_id &&
                                                                                p.from_date === entry.from_date
                                                                        );

                                                                        return (
                                                                            <YearPriceCard
                                                                                key={idx}
                                                                                entry={entry}
                                                                                idx={idx}
                                                                                globalIndex={globalIndex}
                                                                                onPriceChange={handleKitsPriceChange}
                                                                            />
                                                                        );
                                                                    })}
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    );
                                                })}
                                            </Card.Body>
                                        </Card>
                                    ))}

                                    {kitsTotalPages > 1 && (
                                        <DynamicPagination
                                            totalPages={kitsTotalPages}
                                            currentPage={kitsPage}
                                            onPageChange={setKitsPage}
                                            limitPerPage={kitsLimit}
                                            onLimitChange={handleKitsLimitChange}
                                        />
                                    )}

                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <Button type="button" variant="light" onClick={() => navigate(-1)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" variant="primary" disabled={kitsSaving}>
                                            {kitsSaving ? "Saving..." : "Save Price Changes"}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Form>
                    )}
                </Tab>
            </Tabs>
        </div>
    );
}

export default KitPriceChanges;






